import { useEffect, useState } from 'react';
import config from '../config';
import { readSseStream } from '../helpers/sse';

const RECONNECT_MS = 3000;
const FEED_LIMIT = 30;

const time = (iso) => Date.parse(iso) || 0;

/**
 * Folds one message from `/admin/live/trip/:tripId` into the trip's live
 * picture: `{ trip, route, etas, driver, riders }`.
 *
 * `snapshot` replaces the lot; every other event replaces its own part. A
 * driver fix older than the one held is ignored — trip pings and background
 * fixes can arrive out of order. Returns the same object when nothing changed.
 */
export const applyTripLiveEvent = (live, event, data) => {
  switch (event) {
    case 'snapshot':
      return data
        ? { trip: data.trip, route: data.route, etas: data.etas, driver: data.driver, riders: data.riders || [] }
        : live;
    case 'trip':
      return { ...live, trip: data };
    case 'route':
      return { ...live, route: data };
    case 'etas':
      return { ...live, etas: data };
    // Only riders still waiting for pick-up; the server drops the rest.
    case 'riders':
      return { ...live, riders: data || [] };
    case 'driver':
      // `null`: the trip lost its driver, or the new one has no position yet.
      if (!data) return live.driver ? { ...live, driver: null } : live;
      if (live.driver && time(live.driver.timestamp) > time(data.timestamp)) return live;
      return { ...live, driver: data };
    default:
      return live;
  }
};

/**
 * One line per stream message for the live-state feed: what arrived and the
 * one thing about it worth glancing at. Heartbeats aren't worth a line.
 */
export const describeTripEvent = (event, data) => {
  switch (event) {
    case 'snapshot':
      return 'snapshot';
    case 'trip':
      return data
        ? `trip ${data.status} · stop ${(data.currentWaypoint ?? 0) + 1}/${data.waypoints?.length ?? 0}`
        : 'trip';
    case 'route':
      return data ? `route ${(data.distanceMeters / 1609.34).toFixed(1)} mi` : 'route cleared';
    case 'etas':
      return data ? `etas ${data.length} stop${data.length === 1 ? '' : 's'}` : 'etas cleared';
    case 'riders':
      return `riders ${data?.length ?? 0} awaiting pick-up`;
    case 'driver':
      return data
        ? `driver ${data.lat.toFixed(5)}, ${data.lon.toFixed(5)} @ ${Math.round(data.bearing ?? 0)}° (${data.source})`
        : 'driver cleared';
    default:
      return null;
  }
};

const EMPTY = { trip: null, route: null, etas: null, driver: null, riders: [] };

/**
 * Streams one trip live: its stops and their statuses, the whole route the
 * driver is following, the ETA to each remaining stop, and the car.
 */
const useTripLive = (tripId) => {
  const [live, setLive] = useState(EMPTY);
  const [feed, setFeed] = useState([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    setLive(EMPTY);
    setFeed([]);
    if (!tripId) return undefined;

    const controller = new AbortController();
    let retry = null;
    let stopped = false;

    const connect = async () => {
      try {
        await readSseStream({
          url: `${config.api.baseUrl}/admin/live/trip/${encodeURIComponent(tripId)}`,
          signal: controller.signal,
          onOpen: () => setConnected(true),
          onEvent: ({ event, data }) => {
            setLive((current) => applyTripLiveEvent(current, event, data));
            const line = describeTripEvent(event, data);
            if (line) {
              setFeed((current) => [{ at: new Date().toISOString(), line }, ...current].slice(0, FEED_LIMIT));
            }
          },
        });
      } catch (e) {
        if (controller.signal.aborted) return;
      } finally {
        if (!stopped) setConnected(false);
      }
      if (!stopped) retry = setTimeout(connect, RECONNECT_MS);
    };

    connect();

    return () => {
      stopped = true;
      clearTimeout(retry);
      controller.abort();
    };
  }, [tripId]);

  return { ...live, feed, connected };
};

export default useTripLive;
